import { z } from 'zod';
import { IsoTimestampSchema } from './primitives';
/**
 * Public Experiences Part 2 — payment-method domain contract (TYPE-P2-06,
 * owner scope addition 2026-09-16, DEC-P2-18).
 *
 * A Connect platform that creates DIRECT charges must register every web
 * domain that hosts Embedded Checkout PER CONNECTED ACCOUNT, or Apple Pay /
 * Google Pay / Link stay hidden in that account's Checkout. Provisioning those
 * registrations is a permanent part of the connected-account lifecycle:
 *
 *  - automatic after a managed-account migration and after account
 *    provisioning (onboarding, registration, orchestrator);
 *  - a separate, read-only component of the direct-charge eligibility verdict
 *    (`paymentDomains` on the admin and merchant eligibility DTOs);
 *  - recoverable through an explicit "ensure" action (merchant: Payforms
 *    eligibility → "Add Payment Domains"; super-admin: Payment Accounts tab).
 *
 * Stripe is the source of truth for what is registered. The REQUIRED domains
 * come from trusted environment configuration on the server, never from the
 * browser: the ensure request carries only `confirm: true`. Nothing here
 * exposes raw Stripe objects (`pmd_…` ids, `object`, `livemode`): a status is a
 * hostname plus normalized flags.
 *
 * Browser-safe: zod and sibling contract files only.
 */
// ---------------------------------------------------------------------------
// Hostnames
// ---------------------------------------------------------------------------
/**
 * A bare, lowercase hostname (`pointpay-public-dev.web.app`) — never a URL,
 * a path or a wildcard. Stripe registers domains by hostname.
 */
export const PAYMENT_METHOD_DOMAIN_HOSTNAME_REGEX = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/;
export const PaymentMethodDomainHostnameSchema = z
    .string()
    .trim()
    .min(3)
    .max(253)
    .regex(PAYMENT_METHOD_DOMAIN_HOSTNAME_REGEX, 'must be a bare lowercase hostname');
// ---------------------------------------------------------------------------
// Per-domain status (normalized, never a raw Stripe object)
// ---------------------------------------------------------------------------
/**
 * Wallet activation as reported by Stripe on a registered domain, mapped to a
 * closed set. `pending` is reserved for a registration whose validation has
 * not completed; `unknown` when Stripe returned no status for that wallet.
 * Apple Pay stays `inactive` until the domain-association file is hosted at
 * `/.well-known/apple-developer-merchantid-domain-association` on the public
 * site; Link and Google Pay do not need it.
 */
export var PaymentMethodDomainWalletStatusEnum;
(function (PaymentMethodDomainWalletStatusEnum) {
    PaymentMethodDomainWalletStatusEnum["ACTIVE"] = "active";
    PaymentMethodDomainWalletStatusEnum["INACTIVE"] = "inactive";
    PaymentMethodDomainWalletStatusEnum["PENDING"] = "pending";
    PaymentMethodDomainWalletStatusEnum["UNKNOWN"] = "unknown";
})(PaymentMethodDomainWalletStatusEnum || (PaymentMethodDomainWalletStatusEnum = {}));
export const PaymentMethodDomainWalletStatusSchema = z.nativeEnum(PaymentMethodDomainWalletStatusEnum);
export const PaymentMethodDomainWalletsSchema = z
    .object({
    applePay: PaymentMethodDomainWalletStatusSchema.optional(),
    googlePay: PaymentMethodDomainWalletStatusSchema.optional(),
    link: PaymentMethodDomainWalletStatusSchema.optional(),
})
    .strict();
/**
 * One domain as seen from the connected account.
 *
 *  - `registered` — Stripe has a payment-method domain object for it.
 *  - `enabled`    — Stripe's `enabled` flag; `null` when not registered. A
 *                   registered-but-disabled domain does NOT count as verified.
 *  - `wallets`    — present only for registered domains.
 */
export const PaymentMethodDomainStatusSchema = z
    .object({
    domain: PaymentMethodDomainHostnameSchema,
    registered: z.boolean(),
    enabled: z.boolean().nullable(),
    wallets: PaymentMethodDomainWalletsSchema.optional(),
})
    .strict()
    .superRefine((status, ctx) => {
    if (!status.registered && status.enabled !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['enabled'],
            message: 'enabled must be null for an unregistered domain',
        });
    }
    if (!status.registered && status.wallets !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['wallets'],
            message: 'wallets are reported only for registered domains',
        });
    }
});
// ---------------------------------------------------------------------------
// Eligibility component
// ---------------------------------------------------------------------------
/**
 *  - `VERIFIED`             — every required domain is registered AND enabled.
 *  - `MISSING`              — at least one required domain is absent or disabled;
 *                             `missing` lists them. Remediation: ensure.
 *  - `PROVIDER_UNAVAILABLE` — Stripe could not be read. NOT a verdict: nothing
 *                             is claimed about what is registered.
 *  - `NOT_APPLICABLE`       — the account cannot take direct charges (legacy /
 *                             non-direct) or this environment requires no
 *                             domain (local). Nothing to register.
 */
export var PaymentMethodDomainEligibilityStatusEnum;
(function (PaymentMethodDomainEligibilityStatusEnum) {
    PaymentMethodDomainEligibilityStatusEnum["VERIFIED"] = "VERIFIED";
    PaymentMethodDomainEligibilityStatusEnum["MISSING"] = "MISSING";
    PaymentMethodDomainEligibilityStatusEnum["PROVIDER_UNAVAILABLE"] = "PROVIDER_UNAVAILABLE";
    PaymentMethodDomainEligibilityStatusEnum["NOT_APPLICABLE"] = "NOT_APPLICABLE";
})(PaymentMethodDomainEligibilityStatusEnum || (PaymentMethodDomainEligibilityStatusEnum = {}));
export const PaymentMethodDomainEligibilityStatusSchema = z.nativeEnum(PaymentMethodDomainEligibilityStatusEnum);
const sortedUnique = (values) => [...new Set(values)].sort();
const sameSet = (left, right) => {
    const a = sortedUnique(left);
    const b = sortedUnique(right);
    return a.length === b.length && a.every((value, index) => value === b[index]);
};
/**
 * The payment-domain component of the direct-charge verdict.
 *
 * Invariants (enforced by refinement):
 *  - `eligible === (status === 'VERIFIED')`.
 *  - `registered ⊆ required` (only required domains are reported here; the
 *    full account listing lives in `domains`).
 *  - For `VERIFIED`, `MISSING` and `NOT_APPLICABLE`: `missing` is exactly
 *    `required − registered` (as sets). `VERIFIED` therefore has an empty
 *    `missing` and a non-empty `required`; `MISSING` has a non-empty `missing`.
 *  - For `PROVIDER_UNAVAILABLE`: `registered` and `missing` are BOTH empty —
 *    the provider was not read, so the response never claims "no domains" or
 *    "these are missing" — and `message` explains the failure. `missing`
 *    equals `required` only when the status is `MISSING` and nothing is
 *    registered, never as a side effect of a failed read.
 *  - `message` is required for `PROVIDER_UNAVAILABLE` and `NOT_APPLICABLE`.
 */
export const PaymentMethodDomainEligibilitySchema = z
    .object({
    status: PaymentMethodDomainEligibilityStatusSchema,
    eligible: z.boolean(),
    /** Required by trusted server configuration (hostnames, lowercase, unique). */
    required: z.array(PaymentMethodDomainHostnameSchema).max(20),
    /** Required domains that are registered AND enabled in the account. */
    registered: z.array(PaymentMethodDomainHostnameSchema).max(20),
    /** Required domains that are absent or disabled. */
    missing: z.array(PaymentMethodDomainHostnameSchema).max(20),
    /** Every domain Stripe reports for the account (may exceed `required`). */
    domains: z.array(PaymentMethodDomainStatusSchema).max(100),
    checkedAt: IsoTimestampSchema,
    /** Operator-safe explanation; never a provider error body. */
    message: z.string().max(500).nullable(),
})
    .strict()
    .superRefine((value, ctx) => {
    const verified = value.status === PaymentMethodDomainEligibilityStatusEnum.VERIFIED;
    if (value.eligible !== verified) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['eligible'],
            message: 'eligible must be true exactly when status is VERIFIED',
        });
    }
    const requiredSet = new Set(value.required);
    if (!value.registered.every((domain) => requiredSet.has(domain))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['registered'],
            message: 'registered may list required domains only',
        });
    }
    if (value.status ===
        PaymentMethodDomainEligibilityStatusEnum.PROVIDER_UNAVAILABLE) {
        if (value.registered.length > 0 || value.missing.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['missing'],
                message: 'PROVIDER_UNAVAILABLE reports neither registered nor missing domains',
            });
        }
        if (value.message === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['message'],
                message: 'PROVIDER_UNAVAILABLE requires a message',
            });
        }
        return;
    }
    const registeredSet = new Set(value.registered);
    const expectedMissing = value.required.filter((domain) => !registeredSet.has(domain));
    if (!sameSet(value.missing, expectedMissing)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['missing'],
            message: 'missing must equal required minus registered',
        });
    }
    if (verified) {
        if (value.required.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['required'],
                message: 'VERIFIED requires at least one required domain',
            });
        }
        if (expectedMissing.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['status'],
                message: 'VERIFIED cannot have missing domains',
            });
        }
    }
    if (value.status === PaymentMethodDomainEligibilityStatusEnum.MISSING &&
        expectedMissing.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['status'],
            message: 'MISSING requires at least one missing domain',
        });
    }
    if (value.status ===
        PaymentMethodDomainEligibilityStatusEnum.NOT_APPLICABLE &&
        value.message === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['message'],
            message: 'NOT_APPLICABLE requires a message',
        });
    }
});
// ---------------------------------------------------------------------------
// Ensure (remediation) request / response
// ---------------------------------------------------------------------------
/**
 * The browser sends ONLY a confirmation. The account is resolved server-side
 * from the caller's business linkage (merchant) or the `:businessId` path
 * (super-admin); the domains come from server configuration. Any `domain`,
 * `domainName` or `accountId` key is rejected by `.strict()`.
 */
export const EnsurePaymentDomainsRequestSchema = z
    .object({ confirm: z.literal(true) })
    .strict();
export const EnsurePaymentDomainsFailureSchema = z
    .object({
    domain: PaymentMethodDomainHostnameSchema,
    /** Operator-safe reason; never a provider error body. */
    reason: z.string().min(1).max(500),
})
    .strict();
/**
 * Outcome per required domain, then the eligibility re-read AFTER the writes.
 * Idempotent: a second call with everything already in place reports every
 * domain under `alreadyRegistered` and creates nothing.
 */
export const EnsurePaymentDomainsResponseSchema = z
    .object({
    created: z.array(PaymentMethodDomainHostnameSchema).max(20),
    reenabled: z.array(PaymentMethodDomainHostnameSchema).max(20),
    alreadyRegistered: z.array(PaymentMethodDomainHostnameSchema).max(20),
    failed: z.array(EnsurePaymentDomainsFailureSchema).max(20),
    eligibility: PaymentMethodDomainEligibilitySchema,
    /** Stripe's registrations after the writes (same as `eligibility.domains`). */
    paymentDomainsAfter: z
        .array(PaymentMethodDomainStatusSchema)
        .max(100)
        .optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Fixtures (fictitious hostnames; no real accounts)
// ---------------------------------------------------------------------------
export const PXE_FIXTURE_PAYMENT_DOMAIN = 'pointpay-public-example.web.app';
export const PXE_FIXTURE_PAYMENT_DOMAIN_EXTRA = 'pay.example.test';
export const PXE_FIXTURE_PAYMENT_DOMAINS_CHECKED_AT = '2026-09-16T12:00:00.000Z';
export const PXE_FIXTURE_PAYMENT_DOMAIN_STATUS_ACTIVE = {
    domain: PXE_FIXTURE_PAYMENT_DOMAIN,
    registered: true,
    enabled: true,
    wallets: {
        applePay: PaymentMethodDomainWalletStatusEnum.INACTIVE,
        googlePay: PaymentMethodDomainWalletStatusEnum.ACTIVE,
        link: PaymentMethodDomainWalletStatusEnum.ACTIVE,
    },
};
export const PXE_FIXTURE_PAYMENT_DOMAIN_STATUS_MISSING = {
    domain: PXE_FIXTURE_PAYMENT_DOMAIN,
    registered: false,
    enabled: null,
};
export const PXE_FIXTURE_PAYMENT_DOMAINS_VERIFIED = {
    status: PaymentMethodDomainEligibilityStatusEnum.VERIFIED,
    eligible: true,
    required: [PXE_FIXTURE_PAYMENT_DOMAIN],
    registered: [PXE_FIXTURE_PAYMENT_DOMAIN],
    missing: [],
    domains: [PXE_FIXTURE_PAYMENT_DOMAIN_STATUS_ACTIVE],
    checkedAt: PXE_FIXTURE_PAYMENT_DOMAINS_CHECKED_AT,
    message: null,
};
export const PXE_FIXTURE_PAYMENT_DOMAINS_MISSING = {
    status: PaymentMethodDomainEligibilityStatusEnum.MISSING,
    eligible: false,
    required: [PXE_FIXTURE_PAYMENT_DOMAIN],
    registered: [],
    missing: [PXE_FIXTURE_PAYMENT_DOMAIN],
    domains: [],
    checkedAt: PXE_FIXTURE_PAYMENT_DOMAINS_CHECKED_AT,
    message: 'The PointPay checkout domain is not registered on the connected account.',
};
export const PXE_FIXTURE_PAYMENT_DOMAINS_PROVIDER_UNAVAILABLE = {
    status: PaymentMethodDomainEligibilityStatusEnum.PROVIDER_UNAVAILABLE,
    eligible: false,
    required: [PXE_FIXTURE_PAYMENT_DOMAIN],
    registered: [],
    missing: [],
    domains: [],
    checkedAt: PXE_FIXTURE_PAYMENT_DOMAINS_CHECKED_AT,
    message: 'Payment-method domains could not be read from Stripe; try again later.',
};
export const PXE_FIXTURE_PAYMENT_DOMAINS_NOT_APPLICABLE = {
    status: PaymentMethodDomainEligibilityStatusEnum.NOT_APPLICABLE,
    eligible: false,
    required: [PXE_FIXTURE_PAYMENT_DOMAIN],
    registered: [],
    missing: [PXE_FIXTURE_PAYMENT_DOMAIN],
    domains: [],
    checkedAt: PXE_FIXTURE_PAYMENT_DOMAINS_CHECKED_AT,
    message: 'The connected account is a legacy configuration; payment-method domains apply to POINTCRAFT_MANAGED_DIRECT accounts only.',
};
export const PXE_FIXTURE_ENSURE_PAYMENT_DOMAINS_REQUEST = { confirm: true };
export const PXE_FIXTURE_ENSURE_PAYMENT_DOMAINS_RESPONSE = {
    created: [PXE_FIXTURE_PAYMENT_DOMAIN],
    reenabled: [],
    alreadyRegistered: [],
    failed: [],
    eligibility: PXE_FIXTURE_PAYMENT_DOMAINS_VERIFIED,
    paymentDomainsAfter: [PXE_FIXTURE_PAYMENT_DOMAIN_STATUS_ACTIVE],
};
